import { useEffect, useRef, useState } from 'react';

import { CopyIcon, EllipsisVerticalIcon, PencilIcon, Trash2Icon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';

import { RoleNameForm } from './RoleNameForm';

type NameForm = 'rename' | 'duplicate';

export type DuplicateAction = {
  name: string;
  onSubmit: (name: string) => void;
  onOpen?: () => void;
};

type Props = {
  name: string;
  canWrite: boolean;
  canDelete: boolean;
  isRenaming?: boolean;
  duplicate?: DuplicateAction;
  align?: 'start' | 'end';
  triggerClassName?: string;
  onRename: (name: string, opts: { onSuccess: () => void }) => void;
  onEdit?: () => void;
  onDelete: () => void;
};

export const RoleActionsMenu = ({
  name,
  canWrite,
  canDelete,
  isRenaming = false,
  duplicate,
  align = 'end',
  triggerClassName,
  onRename,
  onEdit,
  onDelete,
}: Props) => {
  const { t } = useTranslation();
  const [form, setForm] = useState<NameForm>('rename');
  const [isFormOpen, setIsFormOpen] = useState(false);

  const pendingForm = useRef<NameForm | null>(null);
  const formTimeout = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => () => clearTimeout(formTimeout.current), []);

  const handleMenuClosed = (event: Event) => {
    const next = pendingForm.current;
    if (!next) {
      return;
    }
    event.preventDefault();
    pendingForm.current = null;
    formTimeout.current = setTimeout(() => {
      setForm(next);
      setIsFormOpen(true);
    }, 0);
  };

  const handleRename = (newName: string) => {
    if (newName === name) {
      setIsFormOpen(false);
      return;
    }
    onRename(newName, { onSuccess: () => setIsFormOpen(false) });
  };

  const handleDuplicate = (newName: string) => {
    setIsFormOpen(false);
    duplicate?.onSubmit(newName);
  };

  return (
    <Popover open={isFormOpen} onOpenChange={setIsFormOpen}>
      <DropdownMenu modal={false}>
        <PopoverAnchor asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost-neutral"
              size="icon-sm"
              className={triggerClassName}
              disabled={!canWrite && !canDelete && !onEdit}
              aria-label={t('common.see_more_options')}
            >
              <EllipsisVerticalIcon size={16} />
            </Button>
          </DropdownMenuTrigger>
        </PopoverAnchor>

        <DropdownMenuContent align={align} className="w-45" onCloseAutoFocus={handleMenuClosed}>
          <DropdownMenuItem
            onSelect={() => {
              pendingForm.current = 'rename';
            }}
            disabled={!canWrite}
            className="rounded-lg"
          >
            <PencilIcon size={16} className="text-foreground-muted" />
            {`${t('common.rename')}…`}
          </DropdownMenuItem>

          {onEdit && (
            <DropdownMenuItem onSelect={onEdit} disabled={!canWrite} className="rounded-lg pl-8">
              {`${t('common.edit')}…`}
            </DropdownMenuItem>
          )}

          {duplicate && (
            <DropdownMenuItem
              onSelect={() => {
                pendingForm.current = 'duplicate';
                duplicate.onOpen?.();
              }}
              disabled={!canWrite}
              className="rounded-lg"
            >
              <CopyIcon size={16} className="text-foreground-muted" />
              {`${t('roles.duplicate')}…`}
            </DropdownMenuItem>
          )}

          <DropdownMenuItem
            onSelect={onDelete}
            disabled={!canDelete}
            variant="destructive"
            className="rounded-lg"
          >
            <Trash2Icon size={16} />
            {`${t('common.delete')}…`}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <PopoverContent align={align} className="w-80 p-0">
        {form === 'duplicate' && duplicate ? (
          <RoleNameForm
            key="duplicate"
            title={t('roles.duplicate_custom_role')}
            submitLabel={t('common.continue')}
            defaultName={duplicate.name}
            onSubmit={handleDuplicate}
            onCancel={() => setIsFormOpen(false)}
          />
        ) : (
          <RoleNameForm
            key="rename"
            title={t('roles.rename_custom_role')}
            submitLabel={t('common.update')}
            defaultName={name}
            excludeName={name}
            isSubmitting={isRenaming}
            onSubmit={handleRename}
            onCancel={() => setIsFormOpen(false)}
          />
        )}
      </PopoverContent>
    </Popover>
  );
};
